import { GraphQLClient } from 'graphql-request'
import { apiEndpoint } from './constants'
import { isEmailAvailable as isEmailAvailableQuery, isPhoneAvailable as isPhoneAvailableQuery, uploadUrl } from './queries'
import { confirmUser as confirmUserMutation } from './mutations'
import {
  RuleEngineAlgorithmTrigger,
  RuleEngineFact,
  RuleEngineOperator,
  RuleEngineCondition,
  WeeklyWorkDays,
  ArticleTag,
  Role,
  TextValuePair,
  JobListingExpiration, SalaryType, ApprovalStatus, AgeGroups, JobListingPlan,
  JobListingContactMethod
} from './types'
import _ from 'lodash'
import dayjs from 'dayjs'

/**
 * Revives a Date value from a JSON date string
 */
function reviveDate (key: string, value: object): Date | object {
  let isDate
  if (typeof value === 'string') {
    isDate = /[0-9]{4}-[0-9]{2}-[0-9]{2}T[0-9]{2}:[0-9]{2}:[0-9]{2}.[0-9]{3}Z/.exec(
      value
    )
    if (isDate) {
      return new Date(value)
    }
  }
  return value
}

/**
 * Extracts a time value from a date string
 */
export function formatTime (time: string): string {
  const cleanTime = time.match(/[0-9]{1,2}:[0-9]{2}/)
  const [hours, minutes] = cleanTime ? cleanTime[0].split(':') : []
  return `${hours.padStart(2, '0')}:${minutes}`
}

/**
 * creates a date object from a time string
 */
export function setDateFromTime (time?: string, date: Date = new Date()): Date | undefined {
  if (!time?.valueOf()) {
    return undefined
  }
  const hours = Number(time.split(':')[0])
  const minutes = Number(time.split(':')[1])
  date.setHours(hours, minutes, 0, 0)
  return date
}

/**
 * Makes a graphQL request to the graphQL endpoint
 * @param query the graphQL query
 * @param variables query variables
 * @param jwt current JWT
 */
export async function makeGqlRequest (query: string, variables?: object, jwt?: string) {
  const graphqlClient = new GraphQLClient(apiEndpoint)
  if (jwt) {
    graphqlClient.setHeader('Authorization', `Bearer ${jwt}`)
  }
  const res = await graphqlClient.request(query, variables)
  return JSON.parse(JSON.stringify(res), reviveDate)
}

/**
 * Checks whether a given string represents a valid email address
 * @param {string} email a given email string
 * @returns {boolean} true if the email is valid, false otherwise
 */
export function checkIfEmailIsValid (email: string): boolean {
  return !!email.match(
    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
  )
}

export function checkIfTimeIsValid (time: string): boolean {
  return !!time.match(/^([0-9]|0[0-9]|1[0-9]|2[0-3]):[0-5][0-9]$/)
}

export function checkIfPhoneIsValid (phone: string): boolean {
  return !!phone.match(
    /^(\+972[-]{0,1}|0)(5\d|\d|7\d)([-]{0,1})(\d{3})([-]{0,1})(\d{4})$/
  )
}

export function checkIfUrlIsValid (url: string): boolean {
  return !!url.match(/^((ftp|http|https):\/\/)?[\w#!:.?+=&%@!\-/]+\.[\w#!:.?+=&%@!\-/]+/)
}

export async function checkIfEmailIsAvailable (email: string): Promise<boolean> {
  const { isEmailAvailable } = await makeGqlRequest(isEmailAvailableQuery, { email })
  return isEmailAvailable
}

export async function checkIfPhoneIsAvailable (phone: string): Promise<boolean> {
  const { isPhoneAvailable } = await makeGqlRequest(isPhoneAvailableQuery, { phone })
  return isPhoneAvailable
}

export async function confirmUser (token: string): Promise<boolean> {
  try {
    const { confirmUser } = await makeGqlRequest(confirmUserMutation, { token })
    return confirmUser
  } catch (e) {
    return false
  }
}

export function recursiveReduceCondition (condition: RuleEngineCondition): void {
  const fact = condition.fact as unknown as RuleEngineFact
  const operator = condition.operator as unknown as RuleEngineOperator
  condition.fact = fact?.id
  condition.operator = operator?.id
  if (!condition.children) return
  for (const child of condition.children) {
    recursiveReduceCondition(child)
  }
}

export function translate (word: string): string {
  switch (word) {
    case Role.ADMIN:
      return 'אדמין'
    case Role.AUTHENTICATED:
      return 'משתמש רגיל'
    case Role.EMPLOYER:
      return 'מעסיק'
    case Role.GUIDE:
      return 'מדריך'
    case ApprovalStatus.PENDING:
      return 'ממתין'
    case ApprovalStatus.APPROVED:
      return 'מאושר'
    case ApprovalStatus.REJECTED:
      return 'נדחה'
    case RuleEngineAlgorithmTrigger.MONTH_END:
      return 'סוף חודש'
    case RuleEngineAlgorithmTrigger.SHIFT_DELETE:
      return 'מחיקת משמרת'
    case RuleEngineAlgorithmTrigger.SHIFT_END:
      return 'סוף משמרת'
    case RuleEngineAlgorithmTrigger.SHIFT_START:
      return 'תחילת משמרת'
    case RuleEngineAlgorithmTrigger.WEEK_END:
      return 'סוף שבוע'
    case RuleEngineAlgorithmTrigger.WORK_END:
      return 'גמר חשבון'
    case WeeklyWorkDays.ONE:
      return 'י̶ו̶ם̶ ̶ב̶ש̶ב̶ו̶ע̶'
    case WeeklyWorkDays.TWO_THREE:
      return 'פ̶ע̶מ̶י̶י̶ם̶ ̶ש̶ל̶ו̶ש̶ ̶ב̶ש̶ב̶ו̶ע̶'
    case WeeklyWorkDays.FOUR_FIVE:
      return 'א̶ר̶ב̶ע̶ ̶ח̶מ̶ש̶ ̶פ̶ע̶מ̶י̶ם̶ ̶ב̶ש̶ב̶ו̶ע̶'
    case WeeklyWorkDays.FULLTIME:
      return 'משרה מלאה'
    case WeeklyWorkDays.TEMPORARY:
      return 'עבודה מזדמנת'
    case WeeklyWorkDays.SHIFTS:
      return 'משמרות'
    case ArticleTag.SAFETY_IN_WORKSPACE:
      return 'בטיחות בעבודה'
    case ArticleTag.CONVALESCENCE_FEES:
      return 'דמי הבראה'
    case ArticleTag.SICK_LEAVE:
      return 'דמי מחלה'
    case ArticleTag.NOTICE_TO_EMPLOYEE:
      return 'הודעה לעובד'
    case ArticleTag.THE_RIGHT_TO_WORK_IN_SITTING:
      return 'הזכות לעבודה בישיבה'
    case ArticleTag.COLLECTIVE_AGREEMENTS:
      return 'הסכמים קיבוציים לצעירים ובני נוער'
    case ArticleTag.BREAKS:
      return 'הפסקות'
    case ArticleTag.TRAINING:
      return 'התלמדות, הכשרה, השתלמות, הכנות למשמרת'
    case ArticleTag.CONTRACT_OF_EMPLOYMENT:
      return 'חוזה עבודה'
    case ArticleTag.PROTECTION_OF_YOUTH:
      return 'חוקים והגנות לנוער וצעירים'
    case ArticleTag.TIPS:
      return 'טיפים'
    case ArticleTag.EMPLOYER_EMPLOYEE_RELATIONSHIP:
      return 'יחסי עובד מעביד'
    case ArticleTag.VACATION_DAYS:
      return 'ימי חופשה (חופשה שנתית)'
    case ArticleTag.DEDUCTIONS_AND_FINES:
      return 'ניכויים וקנסות'
    case ArticleTag.TRAVEL:
      return 'נסיעות'
    case ArticleTag.TRANSPORTATION:
      return 'נסיעה, תחבורה, הסעות'
    case ArticleTag.WORK_IN_ELECTION:
      return 'עבודה בבחירות'
    case ArticleTag.WORK_ON_HOLIDAYS:
      return 'עבודה בשבת וחג'
    case ArticleTag.PREGNANCY_AND_BIRTH:
      return 'עבודה, הריון ולידה'
    case ArticleTag.EQUALITY_IN_WORK:
      return 'שיוויון בעבודה ומניעת הטרדה מינית'
    case ArticleTag.MINIMUM_WAGE:
      return 'שכר מינימום'
    case ArticleTag.MINIMUM_WAGE_FOR_YOUTH:
      return 'שכר מינימום לנוער'
    case ArticleTag.OVERTIME:
      return 'שעות נוספות'
    case ArticleTag.WORKING_HOURS_FOR_YOUTH:
      return 'שעות עבודה לבני נוער'
    case ArticleTag.SELF_RESPONSIBILITY:
      return 'אחריות אישית'
    case ArticleTag.NOAL:
      return 'הנוער העובד והלומד'
    case ArticleTag.VICTORIES:
      return 'נצחונות'
    case ArticleTag.CURRENT_AFFAIRS:
      return 'אקטואליה'
    case JobListingExpiration.WEEK:
      return 'שבוע'
    case JobListingExpiration.TWO_WEEKS:
      return 'שבועיים'
    case JobListingExpiration.MONTH:
      return 'חודש'
    case JobListingExpiration.TWO_MONTH:
      return 'חודשיים'
    case JobListingExpiration.THREE_MONTH:
      return 'שלושה חודשים'
    case SalaryType.HOURLY:
      return 'שעתי'
    case SalaryType.MINIMUM:
      return 'מינימום'
    case SalaryType.TIPPED:
      return 'מבוסס טיפים'
    case AgeGroups.EVERYONE:
      return 'הכל'
    case AgeGroups.LESS_THAN_FOURTEEN:
      return 'מתחת ל-14'
    case AgeGroups.FOURTEEN_FIFTEEN:
      return '14-15'
    case AgeGroups.SIXTEEN:
      return '16'
    case AgeGroups.SEVENTEEN:
      return '17'
    case AgeGroups.EIGHTEEN:
      return '18'
    case AgeGroups.NINETEEN_TWENTY:
      return '19-20'
    case AgeGroups.TWENTY_ONE_TWENTY_FIVE:
      return '21-25'
    case AgeGroups.OVER_TWENTY_SIX:
      return '26 ומעלה'
    case JobListingPlan.BASIC:
      return 'בסיסי'
    case JobListingPlan.PRO:
      return 'מתקדם'
    case JobListingPlan.PREMIUM:
      return 'פרימיום'
    case JobListingContactMethod.PHONE_CALL:
      return 'שיחת טלפון'
    case JobListingContactMethod.WHATSAPP_MSG:
      return 'הודעת וואטסאפ'
    case JobListingContactMethod.JOB_APPLICATION:
      return 'שיגישו מועמדות והמעסיק יחזור אליהם'
    default:
      return word
  }
}

export async function uploadImage (image: File): Promise<string> {
  const result: { uploadUrl: string } = await makeGqlRequest(uploadUrl)
  const response = await fetch(result.uploadUrl, {
    method: 'put',
    body: image,
    headers: {
      'Content-Type': 'image/jpeg'
    }
  })
  return response.url.split('?')[0]
}

export function downloadFile (filename: string, text: string): void {
  const element = document.createElement('a')
  element.setAttribute(
    'href',
    'data:text/plain;charset=utf-8,' + encodeURIComponent(text)
  )
  element.setAttribute('download', filename)
  element.style.display = 'none'
  document.body.appendChild(element)
  element.click()
  document.body.removeChild(element)
}

export function convertToCsv (items: unknown[], headers: TextValuePair[]): string {
  const csvRows = items.map(item => {
    return headers.map(header => {
      const value = _.get(item, header.value)
      if (typeof value === 'string') {
        return `"${value.replace(/"/g, '""')}"`
      } else if (value instanceof Date) {
        return `"${Intl.DateTimeFormat('he-IL').format(value)}"`
      } else if (Array.isArray(value)) {
        return `"${value.map(v => typeof v === 'string' ? v.replace(/"/g, '""') : v).join(',')}"`
      }
      return value
    }).join(',')
  })
  csvRows.unshift(headers.map(header => header.text).join(','))
  return csvRows.join('\n')
}

export function getDaysAndHoursBetweenDates (startDate: Date, endDate: Date): { days: number; hours: number } {
  const diffInTime = endDate.getTime() - startDate.getTime()
  const daysPart = Math.floor(diffInTime / (1000 * 3600 * 24))
  const hoursPart = Math.abs(endDate.getUTCHours() - startDate.getUTCHours())
  return { days: daysPart, hours: hoursPart }
}

export function getDaysAgo (date: Date): number {
  const now = new Date()
  const diffInTime = now.getTime() - date.getTime()
  const diffInDays = Math.floor(diffInTime / (1000 * 3600 * 24))
  return diffInDays
}

export function getAge (date: Date | undefined): number | string {
  if (!date) {
    return '-'
  }
  const today = new Date()
  const birthDate = new Date(date)
  let age = today.getFullYear() - birthDate.getFullYear()
  const m = today.getMonth() - birthDate.getMonth()
  if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
    age--
  }
  return age
}

export function getDaysFromNow (date: Date): number {
  const now = new Date()
  const diffInTime = date.getTime() - now.getTime()
  const diffInDays = diffInTime / (1000 * 3600 * 24)
  return diffInDays
}

export function safeSubtract (big?: number, small?: number): number | undefined {
  if ((typeof (big) !== 'number') || (typeof (small) !== 'number')) {
    return undefined
  }
  if (small > big) {
    return undefined
  }
  return big - small
}

export function daysAgo (days: number): Date {
  if (Math.abs(days) === Infinity) {
    return new Date(0)
  }
  const date = new Date()
  date.setDate(date.getDate() - days)
  return date
}

export function daysAgoInISO (days: number): string | undefined {
  if (Math.abs(days) === Infinity || days === 0) {
    return undefined
  }
  const date = daysAgo(days)
  return date?.toISOString().substring(0, 10)
}

export function stringRatio (a?: number, b?: number) {
  if (!a || !b) return '-'
  for (let divisor = b; divisor > 1; divisor--) {
    if (a % divisor === 0 && b % divisor === 0) {
      a = a / divisor
      b = b / divisor
    }
  }
  return a + ':' + b
}

export function convertStringToEnum<T> (enm: { [s: string]: T }, value: string): T | undefined {
  return (Object.values(enm) as unknown as string[]).includes(value)
    ? value as unknown as T
    : undefined
}

export function translatedEnumOptions<T extends { [s: string]: unknown }> (e: T): TextValuePair[] {
  return (Object.values(e) as unknown as string[]).map(key => ({
    value: key,
    text: translate(key)
  }))
}

/**
 * Runs a function after a given delay.
 * If the function is called again before the delay is over, the previous timeout is cancelled and the new one is set.
 * @param {Function} callback the function to run
 * @param {number} timeout the delay in milliseconds
 */
export function debounce (callback: () => Promise<void>, timeoutId: number, timeout = 500): number {
  if (timeoutId) {
    clearTimeout(timeoutId)
  }
  return setTimeout(async () => {
    await callback()
  }, timeout)
}

export const validationRules = {
  requiredArray: (value: unknown): boolean | string => (Array.isArray(value) && value.length > 0) || 'שדה חובה',
  requiredValue: (value: unknown): boolean | string => !!value || 'שדה חובה',
  requiredFile: (value: unknown): boolean | string => !!(value as File)?.name || 'שדה חובה',
  email: (value: unknown): boolean | string => checkIfEmailIsValid(String(value)) || 'כתובת מייל לא תקינה',
  phone: (value: unknown): boolean | string => !value || checkIfPhoneIsValid(String(value)) || 'מספר טלפון לא תקין',
  time: (value: unknown): boolean | string => !value || checkIfTimeIsValid(String(value)) || 'שעה לא תקינה',
  url: (value: unknown): boolean | string => checkIfUrlIsValid(String(value)) || 'כתובת אתר לא תקינה',
  fromToday: (value: unknown): boolean | string => {
    const date = new Date(String(value))
    const today = new Date()
    if (date.getTime() < today.getTime()) {
      return 'התאריך לא יכול להיות לפני היום'
    }
    return true
  }
}

export function translateAgeGroupsToDates (ages?: AgeGroups): { lte?: Date; gt?: Date } {
  switch (ages) {
    case AgeGroups.EVERYONE:
      return { lte: undefined, gt: undefined }
    case AgeGroups.LESS_THAN_FOURTEEN:
      return { lte: undefined, gt: dayjs().subtract(14, 'year').toDate() }
    case AgeGroups.FOURTEEN_FIFTEEN:
      return { lte: dayjs().subtract(14, 'year').toDate(), gt: dayjs().subtract(16, 'year').toDate() }
    case AgeGroups.SIXTEEN:
      return { lte: dayjs().subtract(16, 'year').toDate(), gt: dayjs().subtract(17, 'year').toDate() }
    case AgeGroups.SEVENTEEN:
      return { lte: dayjs().subtract(17, 'year').toDate(), gt: dayjs().subtract(18, 'year').toDate() }
    case AgeGroups.EIGHTEEN:
      return { lte: dayjs().subtract(18, 'year').toDate(), gt: dayjs().subtract(19, 'year').toDate() }
    case AgeGroups.NINETEEN_TWENTY:
      return { lte: dayjs().subtract(19, 'year').toDate(), gt: dayjs().subtract(21, 'year').toDate() }
    case AgeGroups.TWENTY_ONE_TWENTY_FIVE:
      return { lte: dayjs().subtract(21, 'year').toDate(), gt: dayjs().subtract(26, 'year').toDate() }
    case AgeGroups.OVER_TWENTY_SIX:
      return { lte: dayjs().subtract(26, 'year').toDate(), gt: undefined }
    default:
      return { lte: undefined, gt: undefined }
  }
}
